import { Timestamp, toTimestamp } from '@mi-front/core';
import { AuditLiveMention, AuditMatch } from '../../types';

/** Default mention playback window when `duration` is absent (30 seconds). */
const DEFAULT_MENTION_DURATION_MS = 30_000;

type MentionTime = {
  start: string | number | Date;
  end: string | number | Date;
};

/**
 * Checks whether custom mention data contains a usable time range.
 */
function isMentionTime(value: unknown): value is MentionTime {
  if (typeof value !== 'object' || value === null || value instanceof Date) {
    return false;
  }

  const time = value as Record<string, unknown>;
  return (
    (typeof time.start === 'string' ||
      typeof time.start === 'number' ||
      time.start instanceof Date) &&
    (typeof time.end === 'string' || typeof time.end === 'number' || time.end instanceof Date)
  );
}

/**
 * Converts mention `duration` (seconds) to milliseconds, or the 30s default when missing.
 */
function getMentionDurationMs(duration: number | null | undefined): number {
  if (duration == null || duration <= 0) {
    return DEFAULT_MENTION_DURATION_MS;
  }

  return duration * 1000;
}

export interface VerifyMatchPlaybackRange {
  from: Timestamp;
  to: Timestamp;
}

/**
 * Resolves the broadcast playback window for a spot match (detected airing).
 */
export function getSpotMatchPlaybackRange(match: AuditMatch): VerifyMatchPlaybackRange {
  const from = toTimestamp(match.datetime) as Timestamp;
  const to = (from + match.playedDuration) as Timestamp;

  return { from, to };
}

/**
 * Resolves the broadcast playback window for a mention match.
 * Prefers customData.time; otherwise uses `duration` (seconds) from datetime, else 30s.
 */
export function getMentionMatchPlaybackRange(mention: AuditLiveMention): VerifyMatchPlaybackRange {
  const mentionTime = mention.customData?.time;

  if (isMentionTime(mentionTime)) {
    return {
      from: toTimestamp(mentionTime.start) as Timestamp,
      to: toTimestamp(mentionTime.end) as Timestamp,
    };
  }

  const from = toTimestamp(mention.datetime) as Timestamp;
  const to = (from + getMentionDurationMs(mention.duration)) as Timestamp;

  return { from, to };
}
